import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, Badge } from 'reactstrap';
import { ApiRequest, ToastBuilder, ButtonBuilder } from '../../../../../../../../GlobalComponents/Builders';
import { Trash2, List, Code } from 'react-feather';
import Select from 'react-select';

const OPERATORS_BY_TYPE = {
    string:  [{ value: '=', label: 'equals' }, { value: '!=', label: '≠' }, { value: 'contains', label: 'contains' }, { value: 'like', label: 'like' }, { value: 'in', label: 'in list' }, { value: 'not in', label: 'not in' }],
    boolean: [{ value: '=', label: 'equals' }],
    integer: [{ value: '=', label: '=' }, { value: '!=', label: '≠' }, { value: '>', label: '>' }, { value: '>=', label: '≥' }, { value: '<', label: '<' }, { value: '<=', label: '≤' }, { value: 'in', label: 'in list' }, { value: 'not in', label: 'not in' }],
    date:    [{ value: '=', label: '=' }, { value: '!=', label: '≠' }, { value: '>', label: '>' }, { value: '>=', label: '≥' }, { value: '<', label: '<' }, { value: '<=', label: '≤' }],
};

const BOOLEAN_OPTIONS = [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }];

const phaseKey = (phase) => `${phase.phase}_${phase.data_import || ''}`;

// Shared react-select styles
const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 34,
        height: 34,
        fontSize: 13,
        borderRadius: 4,
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13,110,253,.15)' : 'none',
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, padding: '0 6px' }),
    singleValue: (base) => ({ ...base, fontSize: 13 }),
    placeholder: (base) => ({ ...base, fontSize: 13 }),
    option: (base) => ({ ...base, fontSize: 13, padding: '6px 10px' }),
    menu: (base) => ({ ...base, zIndex: 10 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const multiSelectStyles = {
    ...selectStyles,
    control: (base, state) => ({
        ...base,
        minHeight: 34,
        maxHeight: 80,
        overflowY: 'auto',
        fontSize: 13,
        borderRadius: 4,
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13,110,253,.15)' : 'none',
    }),
    valueContainer: (base) => ({ ...base, padding: '2px 6px', gap: 2 }),
    multiValue: (base) => ({ ...base, fontSize: 11, margin: '1px 2px', borderRadius: 3, backgroundColor: undefined, color: undefined }),
    multiValueLabel: (base) => ({ ...base, padding: '1px 4px', fontSize: 11, color: undefined }),
    multiValueRemove: (base) => ({ ...base, padding: '0 2px', color: undefined, backgroundColor: undefined }),
    option: (base) => ({ ...base, fontSize: 13, padding: '6px 10px', backgroundColor: undefined, color: undefined }),
};

const RecordCountsSection = ({
    sections,
    setSections,
    sourceResources = [],
    sourceConnectionId,
    assessmentId,
    onDirtyChange,
}) => {
    const phases = [...(sections.phases?.rows || [])].sort((a, b) => parseFloat(a.phase || 0) - parseFloat(b.phase || 0));
    const recordCounts = sections.record_counts || {};
    const setupRows = sections.setup_resources?.rows || [];

    const [setupCachedValues, setSetupCachedValues] = useState({});
    const [filterSourceIds, setFilterSourceIds] = useState({});
    const activePollers = useRef({}); // Track active poller generation per resource_id

    // Load/reload cached setup values whenever setup rows change
    const setupRowsKey = JSON.stringify(setupRows.map(r => r.resource_id));
    useEffect(() => {
        setupRows.forEach(r => {
            if (r.resource_id) {
                ApiRequest('GET', '/dmt/assessments/{assessmentId}/cache', {
                    pathParams: { assessmentId },
                    queryParams: { conditions: `resource_id = ${r.resource_id}` },
                }).then(res => {
                    const row = (res.data || [])[0];
                    if (row?.cached_ids) {
                        const values = typeof row.cached_ids === 'string' ? JSON.parse(row.cached_ids) : row.cached_ids;
                        if (Array.isArray(values)) {
                            setSetupCachedValues(prev => ({ ...prev, [r.resource_id]: values }));
                        }
                    }
                });
            }
        });
    }, [setupRowsKey]);

    // Load/reload cached IDs for filter source resources
    useEffect(() => {
        const filterSourceResources = sourceResources.filter(r => parseInt(r.is_filter_source) === 1);
        filterSourceResources.forEach(r => {
            ApiRequest('GET', '/dmt/assessments/{assessmentId}/cache', {
                pathParams: { assessmentId },
                queryParams: { conditions: `resource_id = ${r.resource_id}` },
            }).then(res => {
                const row = (res.data || [])[0];
                if (row?.cached_ids) {
                    const ids = typeof row.cached_ids === 'string' ? JSON.parse(row.cached_ids) : row.cached_ids;
                    if (Array.isArray(ids) && ids.length > 0) {
                        setFilterSourceIds(prev => ({ ...prev, [r.resource_id]: { ids, name: r.name, count: ids.length } }));
                    }
                }
            });
        });
    }, [sourceResources]);

    // Combine setup value sources + filter source "all IDs" sources
    const setupValueSources = setupRows
        .filter(r => r.resource_id && (setupCachedValues[r.resource_id] || []).length > 0)
        .map(r => ({
            resource_id: r.resource_id,
            resource_name: r.resource_name,
            type: 'setup',
            options: (setupCachedValues[r.resource_id] || []).map(v => ({ value: String(v.id), label: v.name, name: v.name })),
        }));

    // Add filter source resources as "All [Resource] IDs" options
    const filterSourceSources = Object.entries(filterSourceIds).map(([resourceId, data]) => ({
        resource_id: resourceId,
        resource_name: `All ${data.name} (${data.count})`,
        type: 'filter_source',
        allIds: data.ids,
        options: [], // not selectable individually — it's all or nothing
    }));

    const allValueSources = [...setupValueSources, ...filterSourceSources];

    const [filterableKeys, setFilterableKeys] = useState({});
    const [loadingPhase, setLoadingPhase] = useState(null);

    const loadFilterableKeys = (resourceId) => {
        if (filterableKeys[resourceId]) return;
        ApiRequest('GET', '/dmt/assessments/{assessmentId}/filterableKeys', {
            pathParams: { assessmentId },
            queryParams: { resourceId: String(resourceId) },
        }).then(res => {
                if (res.data?.success) {
                    setFilterableKeys(prev => ({ ...prev, [resourceId]: res.data.keys }));
                }
            });
    };

    const getPhaseResource = (phase) => sourceResources.find(r => r.name === phase.data_import) || null;
    // QUICK FIX: Saved filters may contain 'not_in' from before the operator rename to 'not in'.
    // Normalize on load so the Select finds a matching option. Remove once all saved data is clean.
    const getPhaseFilters = (phase) => (recordCounts[`${phaseKey(phase)}_filters`] || []).map(f =>
        f.op?.includes('_') ? { ...f, op: f.op.replace(/_/g, ' ') } : f
    );
    const getPhaseCount = (phase) => recordCounts[`${phaseKey(phase)}_count`];

    const updateRecordCounts = (updates) => {
        setSections(prev => ({ ...prev, record_counts: { ...(prev.record_counts || {}), ...updates } }));
        if (onDirtyChange) onDirtyChange(true);
    };

    const setPhaseFilters = (phase, filters) => updateRecordCounts({ [`${phaseKey(phase)}_filters`]: filters });

    const addFilterRow = (phase) => {
        setPhaseFilters(phase, [...getPhaseFilters(phase), { field: '', op: '=', value: '', useSetup: false, setupResourceId: '' }]);
    };

    const updateFilterRow = (phase, filterIdx, field, value) => {
        const current = [...getPhaseFilters(phase)];
        current[filterIdx] = { ...current[filterIdx], [field]: value };
        if (field === 'field') {
            current[filterIdx].op = '=';
            current[filterIdx].value = '';
            current[filterIdx].useSetup = false;
            current[filterIdx].setupResourceId = '';
        }
        setPhaseFilters(phase, current);
    };

    const toggleSetupMode = (phase, filterIdx) => {
        const current = [...getPhaseFilters(phase)];
        const f = current[filterIdx];
        const on = !f.useSetup;
        current[filterIdx] = { ...f, useSetup: on, value: '', op: on ? 'in' : '=', setupResourceId: on ? (allValueSources[0]?.resource_id || '') : '' };
        setPhaseFilters(phase, current);
    };

    const removeFilterRow = (phase, filterIdx) => {
        setPhaseFilters(phase, getPhaseFilters(phase).filter((_, i) => i !== filterIdx));
    };

    const getPhaseCustomConditions = (phase) => recordCounts[`${phaseKey(phase)}_conditions`] || '';
    const getPhaseFilterMode = (phase) => recordCounts[`${phaseKey(phase)}_mode`] || 'builder';

    const setPhaseCustomConditions = (phase, value) => updateRecordCounts({ [`${phaseKey(phase)}_conditions`]: value });
    const setPhaseFilterMode = (phase, mode) => updateRecordCounts({ [`${phaseKey(phase)}_mode`]: mode });

    const buildGlobalFilters = (phase, resourceId) => {
        const filters = getPhaseFilters(phase);
        const keys = filterableKeys[resourceId] || [];
        return filters
            .filter(f => f.field && f.value !== '' && (!Array.isArray(f.value) || f.value.length > 0))
            .map(f => {
                const keyMeta = keys.find(k => k.key_name === f.field);
                const filter = { field: f.field, op: f.op, value: f.value };
                if (keyMeta?.filter_param) filter[keyMeta.filter_param] = true;
                if (keyMeta?.data_type === 'boolean') filter.value = f.value === 'true';
                if ((f.op === 'in' || f.op === 'not in') && typeof filter.value === 'string') {
                    filter.value = filter.value.split(',').map(v => v.trim()).filter(Boolean);
                }
                // If using setup values and field ends with /name, swap to /id since we have IDs
                if (f.useSetup && f.setupResourceId && f.field.endsWith('/name')) {
                    filter.field = f.field.replace(/\/name$/, '/id');
                    if (!Array.isArray(filter.value)) {
                        filter.value = filter.value.split(',').map(v => v.trim()).filter(Boolean);
                    }
                }
                // Convert string values to numbers for integer fields or /id fields
                if ((keyMeta?.data_type === 'integer' || filter.field.endsWith('/id')) && Array.isArray(filter.value)) {
                    filter.value = filter.value.map(v => isNaN(v) ? v : Number(v));
                } else if ((keyMeta?.data_type === 'integer' || filter.field.endsWith('/id')) && typeof filter.value === 'string' && !isNaN(filter.value)) {
                    filter.value = Number(filter.value);
                }
                return filter;
            });
    };

    const fetchCount = (phase, phaseIdx) => {
        const resource = getPhaseResource(phase);
        if (!resource || !sourceConnectionId) return;
        const mode = getPhaseFilterMode(phase);
        const globalFilters = mode === 'builder' ? buildGlobalFilters(phase, resource.resource_id) : [];
        const customConditions = mode === 'custom' ? getPhaseCustomConditions(phase) : '';
        const key = phaseKey(phase);

        setLoadingPhase(key);
        ApiRequest('POST', '/dmt/assessments/{assessmentId}/recordCount', {
            pathParams: { assessmentId },
            body: {
                connectionId: sourceConnectionId, resourceUri: resource.resource, resourceId: resource.resource_id, filters: globalFilters,
                ...(customConditions ? { conditions: customConditions } : {}),
            },
        }).then(res => {
            setLoadingPhase(null);
            if (res.data?.success) {
                updateRecordCounts({ [`${key}_count`]: res.data.count });
                const updatedRows = [...phases];
                updatedRows[phaseIdx] = { ...updatedRows[phaseIdx], record_count: String(res.data.count) };
                setSections(prev => ({ ...prev, phases: { rows: updatedRows } }));
                ApiRequest('POST', '/dmt/assessments/{assessmentId}/sections', {
                    pathParams: { assessmentId },
                    body: { section_key: 'phases', section_data: { rows: updatedRows } },
                });
                ToastBuilder('success', res.data.caching
                    ? <>{resource.name}: {res.data.count.toLocaleString()} records<br/><small style={{opacity: 0.8}}>Retrieving IDs...</small></>
                    : `${resource.name}: ${res.data.count.toLocaleString()} records`
                , {}, 5000);

                // If this resource is a filter source, poll for cached IDs after async caching completes
                if (res.data.caching && parseInt(resource.is_filter_source) === 1) {
                    const requestedAt = Math.floor(Date.now() / 1000);
                    const generation = (activePollers.current[resource.resource_id] || 0) + 1;
                    activePollers.current[resource.resource_id] = generation;

                    const pollCache = (attempts = 0) => {
                        if (attempts >= 6) return;
                        if (activePollers.current[resource.resource_id] !== generation) return;
                        const delay = attempts === 0 ? 5000 : 10000;
                        setTimeout(() => {
                            if (activePollers.current[resource.resource_id] !== generation) return;
                            ApiRequest('GET', '/dmt/assessments/{assessmentId}/cache', {
                                pathParams: { assessmentId },
                                queryParams: { conditions: `resource_id = ${resource.resource_id}` },
                            }).then(cacheRes => {
                                if (activePollers.current[resource.resource_id] !== generation) return;
                                const cacheRow = (cacheRes.data || [])[0];
                                if (cacheRow?.cached_at && parseInt(cacheRow.cached_at) >= requestedAt && cacheRow?.cached_ids) {
                                    const ids = typeof cacheRow.cached_ids === 'string' ? JSON.parse(cacheRow.cached_ids) : cacheRow.cached_ids;
                                    if (Array.isArray(ids) && ids.length > 0) {
                                        setFilterSourceIds(prev => ({ ...prev, [resource.resource_id]: { ids, name: resource.name, count: ids.length } }));
                                        ToastBuilder('success', `${resource.name}: ${ids.length} IDs cached`);
                                        return;
                                    }
                                }
                                pollCache(attempts + 1);
                            });
                        }, delay);
                    };
                    pollCache();
                } else if (res.data.cached_ids_count && parseInt(resource.is_filter_source) === 1) {
                    ApiRequest('GET', '/dmt/assessments/{assessmentId}/cache', {
                        pathParams: { assessmentId },
                        queryParams: { conditions: `resource_id = ${resource.resource_id}` },
                    }).then(cacheRes => {
                        const cacheRow = (cacheRes.data || [])[0];
                        if (cacheRow?.cached_ids) {
                            const ids = typeof cacheRow.cached_ids === 'string' ? JSON.parse(cacheRow.cached_ids) : cacheRow.cached_ids;
                            if (Array.isArray(ids)) {
                                setFilterSourceIds(prev => ({ ...prev, [resource.resource_id]: { ids, name: resource.name, count: ids.length } }));
                            }
                        }
                    });
                }
            } else {
                ToastBuilder('error', res.data?.message || 'Failed to get count');
            }
        }).catch(() => { setLoadingPhase(null); ToastBuilder('error', 'Request failed'); });
    };

    useEffect(() => {
        phases.forEach(phase => { const r = getPhaseResource(phase); if (r) loadFilterableKeys(r.resource_id); });
    }, [phases, sourceResources]);

    const phasesWithResources = phases.map((phase, idx) => ({ phase, idx, resource: getPhaseResource(phase) })).filter(p => p.resource && !p.resource.parent_resource_id);

    if (!sourceConnectionId) return <p className='text-muted'>Configure a source connection in Project Overview to enable record counts.</p>;
    if (phasesWithResources.length === 0) return <p className='text-muted'>Add resources to Migration Phases to configure scoped record counts.</p>;

    return (
        <div className='dmt-record-counts'>
            {phasesWithResources.map(({ phase, idx, resource }) => {
                const keys = filterableKeys[resource.resource_id] || [];
                const keyOptions = keys.map(k => ({ value: k.key_name, label: k.label }));
                const filters = getPhaseFilters(phase);
                const count = getPhaseCount(phase);
                const key = phaseKey(phase);
                const filterMode = getPhaseFilterMode(phase);

                return (
                    <Card key={key} className='mb-3' style={{ borderLeft: `3px solid ${count !== undefined ? '#28a745' : '#6c757d'}` }}>
                        <CardBody>
                            <div className='d-flex justify-content-between align-items-center mb-3'>
                                <div className='d-flex align-items-center gap-2'>
                                    <strong>Phase {phase.phase}:</strong>
                                    <span>{resource.name}</span>
                                    {resource.is_filter_source ? <Badge color='info' style={{ fontSize: 10 }}>Filter Source</Badge> : null}
                                    {count !== undefined && <Badge color='success'>{Number(count).toLocaleString()} records</Badge>}
                                </div>
                                <div className='d-flex align-items-center gap-2'>
                                    <div className='btn-group btn-group-sm'>
                                        <button
                                            className={`btn btn-sm ${filterMode === 'builder' ? 'btn-secondary' : 'btn-outline-info'}`}
                                            onClick={() => setPhaseFilterMode(phase, 'builder')}
                                            title='Use filter builder'
                                        >
                                            Builder
                                        </button>
                                        <button
                                            className={`btn btn-sm ${filterMode === 'custom' ? 'btn-secondary' : 'btn-outline-info'}`}
                                            onClick={() => setPhaseFilterMode(phase, 'custom')}
                                            title='Use custom conditions string'
                                        >
                                            <Code size={13} style={{ marginRight: 4, verticalAlign: 'middle', position: 'relative', top: -1 }} />Custom
                                        </button>
                                    </div>
                                    <ButtonBuilder
                                        label={count !== undefined ? 'Update Count' : 'Fetch Count'}
                                        className={count !== undefined ? 'btn-primary btn-sm' : 'btn-primary btn-sm'}
                                        onClick={() => fetchCount(phase, idx)}
                                        useSpinner={loadingPhase === key}
                                        disabled={loadingPhase !== null}
                                    />
                                </div>
                            </div>

                            {filterMode === 'custom' ? (
                                <div>
                                    <textarea
                                        className='form-control'
                                        rows={3}
                                        value={getPhaseCustomConditions(phase)}
                                        onChange={(e) => setPhaseCustomConditions(phase, e.target.value)}
                                        placeholder='e.g. (status/name = "Active" OR status/name = "Pending") AND company/id = 42'
                                        style={{ fontSize: 13, fontFamily: 'monospace' }}
                                    />
                                    <small className='text-muted mt-1 d-block'>
                                        Write native API conditions with full OR/AND/grouping support. Syntax depends on the source system.
                                    </small>
                                </div>
                            ) : (
                                <>
                            {filters.map((filter, filterIdx) => {
                                const selectedKey = keys.find(k => k.key_name === filter.field);
                                const operators = OPERATORS_BY_TYPE[selectedKey?.data_type || 'string'];
                                const canUseSetup = allValueSources.length > 0 && selectedKey?.data_type !== 'boolean' && (filter.op === 'in' || filter.op === 'not in' || filter.useSetup);

                                // Setup mode value input
                                let valueInput;
                                if (selectedKey?.data_type === 'boolean') {
                                    const boolOptions = selectedKey.value_options
                                        ? (typeof selectedKey.value_options === 'string' ? JSON.parse(selectedKey.value_options) : selectedKey.value_options)
                                        : BOOLEAN_OPTIONS;
                                    valueInput = (
                                        <Select
                                            options={boolOptions}
                                            value={boolOptions.find(o => o.value === filter.value) || null}
                                            onChange={(opt) => updateFilterRow(phase, filterIdx, 'value', opt?.value || '')}
                                            placeholder='Select...'
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                            isClearable
                                        />
                                    );
                                } else if (filter.useSetup) {
                                    const source = allValueSources.find(s => String(s.resource_id) === String(filter.setupResourceId));
                                    const isFilterSource = source?.type === 'filter_source';

                                    if (isFilterSource) {
                                        // "All IDs" mode — show badge, auto-set value to all cached IDs
                                        const allIds = source?.allIds || [];
                                        // Auto-populate value if not already set
                                        if (allIds.length > 0 && filter.value !== allIds.map(String).join(',')) {
                                            setTimeout(() => updateFilterRow(phase, filterIdx, 'value', allIds.map(String).join(',')), 0);
                                        }
                                        valueInput = (
                                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                {allValueSources.length > 1 && (
                                                    <div style={{ flex: '0 0 20%' }}>
                                                        <Select
                                                            options={allValueSources.map(s => ({ value: String(s.resource_id), label: s.resource_name }))}
                                                            value={allValueSources.map(s => ({ value: String(s.resource_id), label: s.resource_name })).find(o => o.value === String(filter.setupResourceId)) || null}
                                                            onChange={(opt) => updateFilterRow(phase, filterIdx, 'setupResourceId', opt?.value || '')}
                                                            placeholder='Source...'
                                                            styles={selectStyles}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    </div>
                                                )}
                                                <div style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', gap: 8, height: 34, padding: '0 10px', borderRadius: 4, border: '1px solid rgba(128,128,128,0.2)', fontSize: 13 }}>
                                                    <Badge color='info'>{allIds.length} IDs</Badge>
                                                    <span className='text-muted'>All cached {source?.resource_name?.replace(/\s*\(\d+\)/, '')} IDs will be used</span>
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        // Setup values mode — multi-select
                                        const options = source?.options || [];
                                        const selectedValues = filter.value ? filter.value.split(',').filter(Boolean) : [];
                                        const selectedOptions = options.filter(o => selectedValues.includes(o.value));

                                    valueInput = (
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            {allValueSources.length > 1 && (
                                                <div style={{ flex: '0 0 20%' }}>
                                                    <Select
                                                        options={allValueSources.map(s => ({ value: String(s.resource_id), label: s.resource_name }))}
                                                        value={allValueSources.map(s => ({ value: String(s.resource_id), label: s.resource_name })).find(o => o.value === String(filter.setupResourceId)) || null}
                                                        onChange={(opt) => updateFilterRow(phase, filterIdx, 'setupResourceId', opt?.value || '')}
                                                        placeholder='Source...'
                                                        styles={selectStyles}
                                                        menuPortalTarget={document.body}
                                                    />
                                                </div>
                                            )}
                                            <div style={{ flex: '1 1 auto' }}>
                                                <Select
                                                    isMulti
                                                    closeMenuOnSelect={false}
                                                    options={options}
                                                    value={selectedOptions}
                                                    onChange={(selected) => updateFilterRow(phase, filterIdx, 'value', (selected || []).map(s => s.value).join(','))}
                                                    placeholder={`Select from ${source?.resource_name || 'setup values'}...`}
                                                    styles={multiSelectStyles}
                                                    menuPortalTarget={document.body}
                                                />
                                            </div>
                                        </div>
                                    );
                                    }
                                } else {
                                    valueInput = (
                                        <input
                                            className='form-control'
                                            value={filter.value}
                                            onChange={(e) => updateFilterRow(phase, filterIdx, 'value', e.target.value)}
                                            placeholder={(filter.op === 'in' || filter.op === 'not in') ? 'Comma-separated...' : 'Value...'}
                                            style={{ height: 34, fontSize: 13 }}
                                        />
                                    );
                                }

                                return (
                                    <div key={filterIdx} className='d-flex align-items-start gap-2 mb-2'>
                                        <div style={{ flex: '0 0 18%' }}>
                                            <Select
                                                options={keyOptions}
                                                value={keyOptions.find(o => o.value === filter.field) || null}
                                                onChange={(opt) => updateFilterRow(phase, filterIdx, 'field', opt?.value || '')}
                                                placeholder='Field...'
                                                styles={selectStyles}
                                                menuPortalTarget={document.body}
                                                isClearable
                                            />
                                        </div>
                                        <div style={{ flex: '0 0 8%' }}>
                                            <Select
                                                options={operators}
                                                value={operators.find(o => o.value === filter.op) || null}
                                                onChange={(opt) => updateFilterRow(phase, filterIdx, 'op', opt?.value || '=')}
                                                styles={selectStyles}
                                                menuPortalTarget={document.body}
                                                isSearchable={false}
                                            />
                                        </div>
                                        <div style={{ flex: '1 1 auto' }}>
                                            {valueInput}
                                        </div>
                                        <div className='d-flex align-items-center gap-1' style={{ flex: '0 0 auto', height: 34, paddingTop: 2 }}>
                                            {canUseSetup && (
                                                <button
                                                    className={`btn btn-sm ${filter.useSetup ? 'btn-success' : 'btn-outline-secondary'}`}
                                                    onClick={() => toggleSetupMode(phase, filterIdx)}
                                                    title={filter.useSetup ? 'Switch to free text' : 'Use cached records as reference'}
                                                    style={{ fontSize: 11, padding: '4px 8px', lineHeight: 1.3, whiteSpace: 'nowrap' }}
                                                >
                                                    {filter.useSetup ? 'Using Cached Reference' : 'Use Cached Reference'}
                                                </button>
                                            )}
                                            {allValueSources.length > 0 && !canUseSetup && filter.field && selectedKey?.data_type !== 'boolean' && (
                                                <span className='text-muted' style={{ fontSize: 10 }} title='Use in/not in operator to enable cached values'>
                                                    <List size={13} style={{ opacity: 0.3 }} />
                                                </span>
                                            )}
                                            <Trash2 size={15} style={{ cursor: 'pointer', color: '#dc3545' }} onClick={() => removeFilterRow(phase, filterIdx)} />
                                        </div>
                                    </div>
                                );
                            })}

                            <ButtonBuilder label='Add Filter' className='btn-outline-secondary btn-sm mt-1' onClick={() => addFilterRow(phase)} />
                                </>
                            )}
                        </CardBody>
                    </Card>
                );
            })}
        </div>
    );
};

export default RecordCountsSection;
